<template>
  <OurPage
    :back-route="backRoute"
    class="access-groups"
    :title="pageTitle"
    width="md"
  >
    <DeleteModal
      :show.sync="isShownDeleteModal"
      :article-id="$route.params.articleId"
      :article-name="form.name"
      @deleteArticle="goToList"
    />

    <ArticleForm
      ref="articleForm"
      v-model="form"
      :upload-files="uploadFiles"
      :can-add-subsection="canAddSubsection"
      @uploadFiles="uploadFilesMutation"
      @deleteFiles="deleteFileMutation"
    />

    <template #footer-left>
      <OurButton class="button" :text="$t('button.save')" @click="onClickSaveButton" />
      <OurLink :route="backRoute">
        <OurButton class="button" :text="$t('button.back')" variant="secondary" />
      </OurLink>
    </template>

    <template #footer-right>
      <OurButton
        v-if="getPermission['knowledge_base.delete']"
        class="button"
        variant="thirdary"
        :text="$t('button.remove')"
        @click="onClickDeleteButton"
      />
    </template>
  </OurPage>
</template>

<script>
import ArticleForm from "@/views/settings/base-knowledge/chapter/_components/ArticleForm.vue";
import DeleteModal from "@/views/settings/base-knowledge/chapter/delete/Modal.vue";
import apolloMixin from "@/mixins/apollo.mixin";
import { articleQuery } from "@/views/settings/base-knowledge/chapter/edit/gql/queries";
import {
  deleteFileMutation,
  uploadFilesMutation,
} from "@/views/settings/base-knowledge/chapter/add/gql/mutations";
import { updateArticleMutation } from "@/views/settings/base-knowledge/chapter/edit/gql/mutations";
import { deleteArticleMutation } from "@/views/settings/base-knowledge/chapter/delete/gql/mutations";
import { createKnowledgeBaseSectionMutation } from "@/views/settings/base-knowledge/add/gql/mutations";
import { mapGetters } from "vuex";
import {
  canCreateSubsectionAtDepth,
  getKnowledgeBaseSectionMeta,
} from "@/views/settings/base-knowledge/services/sections";

export default {
  name: "ChapterEdit",
  components: { DeleteModal, ArticleForm },
  mixins: [apolloMixin],
  data() {
    return {
      isShownDeleteModal: false,
      sectionDepth: null,
      sectionName: "",
      form: {
        name: "",
        type: "",
        content: "",
        files: [],
        visibleForDealers: false,
      },
      uploadFiles: [],
    };
  },
  computed: {
    ...mapGetters("user", ["getPermission"]),

    canAddSubsection() {
      return canCreateSubsectionAtDepth(this.sectionDepth);
    },

    pageTitle() {
      if (this.form.type === "subsection") {
        return this.$t("title.knowledgeBase.edit");
      }

      return this.$t("title.knowledgeBase.editChapter");
    },

    backRoute() {
      return {
        name: "Chapter",
        params: { id: this.$route.params.id },
        title: this.sectionName || this.$t("title.knowledgeBase.chapter"),
      };
    },
  },
  created() {
    this.getData();
    this.loadSectionMeta();
  },
  methods: {
    async loadSectionMeta() {
      const sectionMeta = await getKnowledgeBaseSectionMeta(this.$route.params.id);

      this.sectionDepth = sectionMeta.depth;
      this.sectionName = sectionMeta.name || "";
    },

    async getData() {
      const { article } = await this.$get(articleQuery, {
        id: this.$route.params.articleId,
      });

      this.form = {
        ...article,
      };
    },

    async uploadFilesMutation(selectedFiles) {
      const { uploadFiles: files } = await this.$post(
        uploadFilesMutation,
        { files: selectedFiles },
        { withLoader: false, withNotify: false }
      );

      this.uploadFiles = files;
    },

    async deleteFileMutation(id) {
      await this.$post(deleteFileMutation, { id }, { withLoader: false, withNotify: false });
    },

    async onClickSaveButton() {
      this.$refs.articleForm.emitForm();

      if (!this.form.isValid) {
        return;
      }

      if (this.form.type === "subsection") {
        const { createKnowledgeBaseSection } = await this.$post(
          createKnowledgeBaseSectionMutation,
          {
            name: this.form.name,
            visibleForDealers: this.form.visibleForDealers,
            parentId: this.$route.params.id,
          },
          {
            delaySuccessNotify: true,
          }
        );

        if (!createKnowledgeBaseSection?.id) {
          return;
        }

        await this.$post(
          deleteArticleMutation,
          { id: this.$route.params.articleId },
          { withNotify: false }
        );

        await this.$router.push({ name: "Chapter", params: { id: this.$route.params.id } });

        return;
      }

      const { updateArticle } = await this.$post(
        updateArticleMutation,
        { id: this.$route.params.articleId, sectionId: this.$route.params.id, ...this.form },
        {
          delaySuccessNotify: true,
        }
      );

      if (updateArticle.id) await this.$router.push({ name: "Chapter" });
    },
    onClickDeleteButton() {
      this.isShownDeleteModal = true;
    },
    goToList() {
      this.$router.push({ name: "Chapter" });
    },
  },
};
</script>
